import React, { useMemo } from "react";
import styled from "@emotion/styled";

import StatTypes from "types/StatTypes";

import CommonUtils from "services/common/utils";
import ParkkiTheme from "themes/ParkkiTheme";

import InfosTooltip from "components/InfosTooltip";
import StatsContainer from "components/StatsContainer";

const NormalUnity = styled.label`
  font-size: 12px;
`;

const Normal = styled.div`
  display: flex;
  align-items: baseline;
  text-align: center;
  font-size: 16px;
  margin: 4px;
  color: ${(props: { color: string }) => props.color};
`;

const Percentage = styled.div`
  display: flex;
  align-items: baseline;
  text-align: center;
  font-size: 12px;
  padding: 2px 4px;
  margin-top: 12px;
  border: 1px solid rgb(39, 174, 96);
  border-radius: 5px;
  color: ${(props: { color: string }) => props.color};
  border-color: ${(props: { color: string }) => props.color};
`;

const ArrowImage = styled.img`
  width: 10px;
  height: auto;
  margin-right: 8px;
`;

const Label = styled.div`
  font-size: 10px;
  width: 80%;
  text-align: center;
  margin-bottom: 4px;
`;

const SubLabel = styled.div`
  font-size: 10px;
  height: 10px;
`;

const ValueUnity = styled.label`
  font-size: 11px;
  margin-left: 3px;
`;

const Value = styled.div`
  color: ${ParkkiTheme.colors.lightBlue};
  font-size: 21px;
  font-weight: bold;
  margin-bottom: 4px;
`;

type Props = { data: StatTypes; numberOfSection: number };

function PercentageStats({ data, numberOfSection }: Props): JSX.Element {
  const value = useMemo(() => {
    return data?.value?.formatValue &&
      data?.value?.value !== null &&
      data?.value?.value !== undefined
      ? data?.value?.formatValue(data?.value?.value)
      : data?.value?.value;
  }, [data?.value]);

  const subValue = useMemo(() => {
    return data?.type?.retrieveSubvalue
      ? data?.type?.retrieveSubvalue(data?.value?.value)
      : data?.value;
  }, [data?.type, data?.value]);

  const percentage = useMemo(() => {
    return data?.type?.retrievePercentage
      ? data?.type?.retrievePercentage(data?.value?.value, subValue)
      : data?.value;
  }, [data?.value, subValue, data?.type]);

  const Symbol = useMemo(() => {
    const symbol = data?.type?.symbol;
    const way = data?.type?.way
      ? data?.type?.way(data?.value?.value, data?.value?.avg)
      : "";
    const color = data?.type?.retrieveColor
      ? data?.type?.retrieveColor(data?.value?.value, data?.value?.avg)
      : "";

    if (symbol === "arrow") {
      switch (way) {
        case 1:
          return (
            <ArrowImage
              alt={`arrow-up-${color}`}
              src={require(`images/stats-row-arrow-up-${color}.png`)}
            />
          );
        case -1:
          return (
            <ArrowImage
              alt={`arrow-down-${color}`}
              src={require(`images/stats-row-arrow-down-${color}.png`)}
            />
          );
        default:
          return "=";
      }
    } else if (symbol === "sign") {
      switch (way) {
        case 1:
          return "+";
        case -1:
          return "-";
        default:
          return "=";
      }
    } else {
      return "";
    }
  }, [data?.type, data?.value]);

  const subValueColor = useMemo(() => {
    switch (
      data.type.retrieveColor
        ? data?.type?.retrieveColor(data?.value?.value, data?.value?.avg)
        : ""
    ) {
      case "green":
        return ParkkiTheme.colors.green;
      case "red":
        return ParkkiTheme.colors.red;
      case "orange":
        return ParkkiTheme.colors.orange;
      default:
        return "black";
    }
  }, [data?.value, data?.type]);

  const percentageColor = useMemo(() => {
    if (percentage >= 0) {
      return ParkkiTheme.colors.green;
    }

    return ParkkiTheme.colors.red;
  }, [percentage]);

  const percentageToPrint = useMemo(() => {
    return data?.type?.formatPercentage
      ? data?.type?.formatPercentage(percentage)
      : percentage;
  }, [data?.type, percentage]);

  if (!data?.toggle) return <></>;

  return (
    <StatsContainer
      disabled={value === null || value === undefined}
      headerColor={data?.headerColor}
      order={data?.type?.order}
      numberOfSection={numberOfSection}
      style={{ paddingTop: "3px", paddingBottom: "3px" }}
    >
      <>
        <Value>
          {value !== null && value !== undefined
            ? CommonUtils.formatNumber(value)
            : "-"}
          <ValueUnity>{data?.type?.valueUnity}</ValueUnity>
        </Value>
        <Label>{(data?.label || "").toUpperCase()}</Label>
        <SubLabel>{data?.subLabel || ""}</SubLabel>

        {data?.type?.toggle &&
        percentage !== null &&
        percentage !== undefined ? (
          <Percentage color={percentageColor}>
            {percentage > 0 ? "+ " : ""}
            {Number.isNaN(percentageToPrint)
              ? "- "
              : CommonUtils.formatNumber(percentageToPrint)}
            <NormalUnity> %</NormalUnity>
          </Percentage>
        ) : null}

        {data?.type?.toggle && subValue !== null && subValue !== undefined ? (
          <>
            <Normal color={subValueColor}>
              {Symbol}{" "}
              {data?.value?.formatSubvalue
                ? CommonUtils.formatNumber(
                    data?.value?.formatSubvalue(subValue)
                  )
                : CommonUtils.formatNumber(subValue)}
              <NormalUnity> {data?.type?.unity}</NormalUnity>
              {data?.infos ? <InfosTooltip infos={data?.infos} /> : null}
            </Normal>

            <SubLabel>{data?.type?.subvalueLabel}</SubLabel>
          </>
        ) : null}
      </>
    </StatsContainer>
  );
}

export default PercentageStats;
